import React from 'react';
import { Icon } from '../icons';
import { cn } from '@shared/lib/utils';

export interface RatingDistribution {
  /**
   * Number of 5-star ratings
   */
  fiveStars: number;
  /**
   * Number of 4-star ratings
   */
  fourStars: number;
  /**
   * Number of 3-star ratings
   */
  threeStars: number;
  /**
   * Number of 2-star ratings
   */
  twoStars: number;
  /**
   * Number of 1-star ratings
   */
  oneStar: number;
}

export interface RatingBreakdownProps {
  /**
   * Distribution of ratings across 1-5 stars
   */
  distribution: RatingDistribution;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Width of the progress bar
   * @default '200px'
   */
  barWidth?: string;
  /**
   * Height of the progress bar
   * @default '15px'
   */
  barHeight?: string;
  /**
   * Gap between rating rows
   * @default 'gap-1'
   */
  rowGap?: string;
  /**
   * Font size for rating numbers
   * @default 'text-xl'
   */
  numberSize?: string;
  /**
   * Font size for review counts
   * @default 'text-xl'
   */
  countSize?: string;
  /**
   * Color for rating numbers and stars
   * @default 'text-feedback-success'
   */
  accentColor?: string;
  /**
   * Background color for progress bar
   * @default 'bg-[#e1f7db]'
   */
  barBgColor?: string;
  /**
   * Fill color for progress bar
   * @default 'bg-feedback-success'
   */
  barFillColor?: string;
  /**
   * Color for review count text
   * @default 'text-secondary'
   */
  countColor?: string;
}

interface RatingRowProps {
  stars: number;
  count: number;
  percentage: number;
  barWidth: string;
  barHeight: string;
  numberSize: string;
  countSize: string;
  accentColor: string;
  barBgColor: string;
  barFillColor: string;
  countColor: string;
}

const RatingRow: React.FC<RatingRowProps> = ({
  stars,
  count,
  percentage,
  barWidth,
  barHeight,
  numberSize,
  countSize,
  accentColor,
  barBgColor,
  barFillColor,
  countColor,
}) => {
  return (
    <div className="flex items-center gap-2.5">
      {/* Star number */}
      <span
        className={cn(
          'font-medium leading-none min-w-[12px]',
          numberSize,
          accentColor
        )}
      >
        {stars}
      </span>

      {/* Star icon */}
      <Icon name="star" size="sm" className={accentColor} />

      {/* Progress bar */}
      <div
        className={cn('relative rounded-full overflow-hidden', barBgColor)}
        style={{ width: barWidth, height: barHeight }}
      >
        <div
          className={cn(
            'absolute top-0 left-0 h-full rounded-full transition-all duration-300',
            barFillColor
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Review count */}
      <span className={cn('font-medium leading-none', countSize, countColor)}>
        ({count})
      </span>
    </div>
  );
};

/**
 * RatingBreakdown component
 *
 * Displays a breakdown of ratings from 1 to 5 stars with progress bars
 * showing the distribution of reviews.
 *
 * @example
 * ```tsx
 * <RatingBreakdown
 *   distribution={{
 *     fiveStars: 22,
 *     fourStars: 1,
 *     threeStars: 0,
 *     twoStars: 0,
 *     oneStar: 0,
 *   }}
 * />
 * ```
 */
export const RatingBreakdown: React.FC<RatingBreakdownProps> = ({
  distribution,
  className,
  barWidth = '200px',
  barHeight = '15px',
  rowGap = 'gap-1',
  numberSize = 'text-xl',
  countSize = 'text-xl',
  accentColor = 'text-feedback-success',
  barBgColor = 'bg-[#e1f7db]',
  barFillColor = 'bg-feedback-success',
  countColor = 'text-secondary',
}) => {
  // Calculate total reviews
  const totalReviews =
    distribution.fiveStars +
    distribution.fourStars +
    distribution.threeStars +
    distribution.twoStars +
    distribution.oneStar;

  // Calculate percentage for each rating
  const calculatePercentage = (count: number) => {
    if (totalReviews === 0) return 0;
    return (count / totalReviews) * 100;
  };

  const ratings = [
    { stars: 5, count: distribution.fiveStars },
    { stars: 4, count: distribution.fourStars },
    { stars: 3, count: distribution.threeStars },
    { stars: 2, count: distribution.twoStars },
    { stars: 1, count: distribution.oneStar },
  ];

  return (
    <div className={cn('flex flex-col', rowGap, className)}>
      {ratings.map(({ stars, count }) => (
        <RatingRow
          key={stars}
          stars={stars}
          count={count}
          percentage={calculatePercentage(count)}
          barWidth={barWidth}
          barHeight={barHeight}
          numberSize={numberSize}
          countSize={countSize}
          accentColor={accentColor}
          barBgColor={barBgColor}
          barFillColor={barFillColor}
          countColor={countColor}
        />
      ))}
    </div>
  );
};
